import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MonEtablissementPageActions } from '@features/compte/mon-etablissement/features/mon-etablissement-detail/store/actions/mon-etablissement.actions';
import { catchError, finalize, map, of, startWith, switchMap, tap } from 'rxjs';
import { EntrepriseService } from '@shared/services/entreprise/entreprise.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Update } from '@ngrx/entity';
import { OrganismeFormation } from '@features/referentiel/models/organisme-formation';
import { EntiteActions } from '@features/referentiel/actions/entite.actions';
import { MessageService } from 'primeng/api';

@Injectable()
export class MonEtablissementEffects {
  loadIbans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonEtablissementPageActions.loadIbans),
      switchMap(({ siret }) =>
        this.entrepriseService.getIbansCertification(siret).pipe(
          map((ibans) => MonEtablissementPageActions.loadIbansSuccess({ ibans })),
          catchError((error) =>
            of(
              MonEtablissementPageActions.loadIbansFailure({
                errorMessage: error?.message ?? this.translate.instant('etablissement.errors.load'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  addIban$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonEtablissementPageActions.addIban),
      switchMap(({ siret, iban }) =>
        this.entrepriseService.addIbanForMonEtablissement(siret, iban).pipe(
          map((createdIban) => MonEtablissementPageActions.addIbanSuccess({ iban: createdIban })),
          catchError((error) =>
            of(
              MonEtablissementPageActions.addIbanFailure({
                errorMessage:
                  error?.response?.data?.message ??
                  error?.error?.message ??
                  error?.error?.errorMessage ??
                  error?.message ??
                  this.translate.instant('modal.add_iban.errors.creation'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  addIbanSuccessNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MonEtablissementPageActions.addIbanSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            detail: this.translate.instant('modal.add_iban.notification.created'),
          });
        }),
      ),
    { dispatch: false },
  );

  sendEtablissement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonEtablissementPageActions.clickEnvoyer),
      switchMap((action) => {
        return this.entrepriseService.sendEtablissement(action.form).pipe(
          tap(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('toast.my_establishment.title'),
              detail: this.translate.instant('toast.my_establishment.details'),

              data: { redirectUrl: ['mes-etablissements'] },
              sticky: true,
              closable: false,
            });
          }),
          map(() => MonEtablissementPageActions.clickEnvoyerSuccess({ form: action.form })),
          catchError((error) =>
            of(MonEtablissementPageActions.clickEnvoyerFailure({ errorMessage: error.message })),
          ),
          startWith(MonEtablissementPageActions.updateLoading({ isLoading: true })),
          finalize(() => this.store.dispatch(MonEtablissementPageActions.updateLoading({ isLoading: false })))
        );
      }),
    ),
  );
  sendEtablissementSucess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MonEtablissementPageActions.clickEnvoyerSuccess),
      switchMap((action) => {
        const update: Update<OrganismeFormation> = {
          id: action.form.identifiantTechnique,
          changes: {
            assujettissementTva: action.form.assujetissementTVA,
            tauxTva: action.form.tauxTVA != null ? `${action.form.tauxTVA}%` : action.form.tauxTVA,
            numTvaIntracommunautaire: action.form.numTvaIntracommunautaire,
            delaiPaiement: action.form.paymentDeadline?.split(' ')[0],
            tauxPenalite: action.form.penaltyRate!,
          },
        };

        this.store.dispatch(EntiteActions.updateEntite({ update }));

        return of(MonEtablissementPageActions.noAction());
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly entrepriseService: EntrepriseService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
  ) {}
}
